import { useMemo, useState } from "react";

/** Comparador común: vacíos al final, números y booleanos reales, texto con locale es. */
function compare(va, vb, dir) {
  const empty = (v) => v === null || v === undefined || v === "" || Number.isNaN(v);
  if (empty(va) && empty(vb)) return 0;
  if (empty(va)) return 1;
  if (empty(vb)) return -1;
  let r;
  if (typeof va === "number" && typeof vb === "number") r = va - vb;
  else if (typeof va === "boolean" || typeof vb === "boolean") r = (va ? 1 : 0) - (vb ? 1 : 0);
  else r = String(va).localeCompare(String(vb), "es", { numeric: true, sensitivity: "base" });
  return dir === "asc" ? r : -r;
}

/** Ordena un array sin estado (para tablas repetidas dentro de un .map). */
export function sortRows(rows, sortField, sortDir, accessors = {}) {
  const list = Array.isArray(rows) ? rows : [];
  if (!sortField) return list;
  const get = accessors[sortField] || ((r) => r?.[sortField]);
  return [...list].sort((a, b) => compare(get(a), get(b), sortDir));
}

/** Sólo el estado de orden: útil cuando varias tablas comparten cabecera. */
export function useSortState(initialField = null, initialDir = "desc") {
  const [sortField, setSortField] = useState(initialField);
  const [sortDir, setSortDir] = useState(initialDir);
  const toggle = (field) => {
    if (field === sortField) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("desc"); }
  };
  return { sortField, sortDir, toggle };
}

/**
 * Ordena cualquier listado desde la cabecera.
 * accessors: { campo: (fila) => valor } para columnas calculadas.
 * El primer clic ordena de mayor a menor (y de más reciente a más antiguo).
 */
export function useTableSort(rows, accessors = {}, initialField = null, initialDir = "desc") {
  const [sortField, setSortField] = useState(initialField);
  const [sortDir, setSortDir] = useState(initialDir);

  const toggle = (field) => {
    if (field === sortField) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("desc"); }
  };

  const sorted = useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    if (!sortField) return list;
    const get = accessors[sortField] || ((r) => r?.[sortField]);
    return [...list].sort((a, b) => compare(get(a), get(b), sortDir));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortField, sortDir]);

  return { sorted, sortField, sortDir, toggle };
}

/** Cabecera clicable. `sort` es lo que devuelve useTableSort. */
export const SortTh = ({ field, sort, className = "", align = "left", title, children, testid }) => {
  const active = sort.sortField === field;
  const justify = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
  return (
    <th
      className={`cursor-pointer select-none hover:text-slate-900 ${className}`}
      onClick={() => sort.toggle(field)}
      title={title || "Ordenar por esta columna"}
      data-testid={testid || `sort-${field}`}
    >
      <span className={`inline-flex w-full items-center gap-1 ${justify}`}>
        {children}
        <span className={active ? "text-slate-900" : "text-slate-300"} aria-hidden>
          {active ? (sortDirArrow(sort.sortDir)) : "↕"}
        </span>
      </span>
    </th>
  );
};

const sortDirArrow = (dir) => (dir === "asc" ? "↑" : "↓");
